/**
 * Schedule — when this building was last seen and when it is due (EP-004P).
 *
 * == Relative dates, because the date is not the decision =================
 *
 * "2 Oct 2026" requires arithmetic. "in 36 days" is the answer. Both are
 * shown — the absolute date because a provider coordinates with other people
 * who use dates, the relative one because that is what decides whether to go.
 *
 * == On freshness ========================================================
 *
 * There is no "synced 5 minutes ago" indicator here, and its absence is
 * deliberate. The ingest does not maintain `updated_at`, so any freshness
 * claim derived from the row would be fiction. What IS trustworthy is the
 * census date the facility itself recorded, so that is what is shown, labelled
 * as what it is.
 *
 * == Several rows ========================================================
 *
 * The service returns scheduling as a list. One row per facility is expected,
 * but nothing enforces it, and picking one silently would hide a data
 * condition worth seeing. If there are several, all are rendered.
 */

import React from 'react';
import { DaySheet, SchedulingRow, VisitPlan, shortDate, relativeDay, daysFromToday, manifestLinkOf } from './providerApi';
import { StateBlock as TideState } from '../../components/tide';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { SNF_CREDENTIALING, SCHEDULING_MANIFEST_UPLOADED } from '../../theme/mondayPalette';

/**
 * Tone for a date that has a deadline meaning.
 *
 * `overdueAfter` days past the date turns it red. A next-visit date in the
 * past means the visit did not happen; a census taken a long time ago means
 * the roster below may not be who is actually in the building.
 */
function tone(value: string | null, kind: 'due' | 'age'): '' | 'attn' | 'stop' {
  const days = daysFromToday(value);
  if (days === null) return '';
  if (kind === 'due') {
    if (days < 0) return 'stop';
    if (days <= 2) return 'attn';
    return '';
  }
  // age: how stale is this
  if (days < -45) return 'stop';
  if (days < -21) return 'attn';
  return '';
}

function Fact({
  label, value, kind, note,
}: {
  label: string;
  value: string | null;
  kind?: 'due' | 'age';
  /** Where the date came from, when that changes what the provider should trust. */
  note?: string | null;
}) {
  const rel = relativeDay(value);
  const t = kind ? tone(value, kind) : '';
  return (
    <div className="pv-fact">
      <dt>{label}</dt>
      <dd className={value ? '' : 'none'}>
        {value ? shortDate(value) : 'Not recorded'}
        {rel && <span className={`rel${t ? ` ${t}` : ''}`}>{rel}</span>}
        {note && <span className="src">{note}</span>}
      </dd>
    </div>
  );
}

/**
 * P1-D: which date the provider sees. The plan Scheduling keeps in eMerge
 * wins; the board mirror is shown only where no plan exists yet, and says so.
 */
function nextVisit(plan: VisitPlan | null | undefined, row: SchedulingRow | null): { value: string | null; note: string | null } {
  if (plan?.next) {
    const who = plan.next.provider_name ? ` · ${plan.next.provider_name}` : '';
    return { value: plan.next.planned_on, note: `${plan.next.state === 'CONFIRMED' ? 'Confirmed' : 'Planned'} in eMerge${who}` };
  }
  if (row?.provider_next_visit_at) return { value: row.provider_next_visit_at, note: 'From the board · no eMerge plan yet' };
  return { value: null, note: null };
}
function lastVisit(plan: VisitPlan | null | undefined, row: SchedulingRow | null): { value: string | null; note: string | null } {
  if (plan?.last?.completed_on) {
    const who = plan.last.provider_name ? ` · ${plan.last.provider_name}` : '';
    return { value: plan.last.completed_on, note: `Completed in eMerge${who}` };
  }
  if (row?.date_of_last_visit) return { value: row.date_of_last_visit, note: 'From the board' };
  return { value: null, note: null };
}

/**
 * The manifest link.
 *
 * This is the workbook a provider charts in, so it gets a real target rather
 * than being buried in a field list. `rel="noreferrer"` matters more than
 * usual here: the URL may be pre-authenticated, and a Referer header would
 * hand it to whatever the provider visits next.
 *
 * A missing link is stated, not hidden. "No manifest recorded" is a fact the
 * scheduling team needs to see; an absent row would just look like a tidy
 * screen.
 */
function ManifestLink({ link }: { link: { url: string; reference: boolean } | null }) {
  if (!link) {
    return (
      <TideState
        kind="empty"
        reason="not-recorded"
        message="No manifest recorded for this facility. If you chart here, the link is missing from the scheduling board rather than from this screen."
      />
    );
  }
  const { url } = link;
  return (
    <div className="pv-row">
      <div className="who">
        <strong>Cloud manifest</strong>
        <span>
          {link.reference
            ? 'Monday’s link, shown for reference: eMerge does not hold this building’s link yet. Scheduling sets it on the board.'
            : 'The workbook you chart in for this facility.'}
        </span>
      </div>
      <div className="aside">
        <a
          className="pv-link"
          href={url}
          target="_blank"
          rel="noreferrer noopener"
        >
          Open manifest
        </a>
      </div>
    </div>
  );
}

function ScheduleCard({ row, index, total, plan, link }: { row: SchedulingRow; index: number; total: number; plan?: VisitPlan | null; link: { url: string; reference: boolean } | null }) {
  const next = nextVisit(plan, row);
  const last = lastVisit(plan, row);
  return (
    <section className="pv-card">
      <div className="pv-card-head">
        <div>
          <h2>{total > 1 ? `Scheduling record ${index + 1} of ${total}` : 'Visit schedule'}</h2>
          <p className="why">
            {row.status
              ? `Facility status: ${row.status}`
              : 'No facility status recorded'}
          </p>
        </div>
        {row.manifest_uploaded && (
          <FilledStatusChip word={row.manifest_uploaded} column={SCHEDULING_MANIFEST_UPLOADED} label={`manifest ${row.manifest_uploaded}`} size="sm" />
        )}
      </div>

      {/* The building's link (eMerge's first), once: it is the building's, not a scheduling row's. */}
      {index === 0 && <ManifestLink link={link} />}

      <dl className="pv-facts">
        <Fact label="Last visit" value={last.value} kind="age" note={last.note} />
        <Fact label="Next visit" value={next.value} kind="due" note={next.note} />
        <Fact label="Tentative next" value={row.tentative_next_visit} />
        <Fact label="Census taken" value={row.census_date} kind="age" />
        {/* TM follow-up is not shown: there is no territory manager to follow
            up (April Torino, 2026-09-08). The field stays in the payload; if
            the role returns, the fact returns with it. */}
      </dl>

      {row.comments && row.comments.trim() && (
        <p className="pv-comment">{row.comments.trim()}</p>
      )}
    </section>
  );
}

export default function Schedule({ sheet }: { sheet: DaySheet }) {
  const { scheduling, facility } = sheet;
  const plan = sheet.visitPlan || null;
  const planned = Boolean(plan && (plan.next || plan.last));
  const link = manifestLinkOf(sheet);

  return (
    <>
      {scheduling.length === 0 && planned ? (
        // P1-D: a building Scheduling plans in eMerge that the board never carried.
        <section className="pv-card">
          <div className="pv-card-head">
            <div>
              <h2>Visit schedule</h2>
              <p className="why">Planned in eMerge by Scheduling.</p>
            </div>
          </div>
          <ManifestLink link={link} />
          <dl className="pv-facts">
            <Fact label="Last visit" value={lastVisit(plan, null).value} kind="age" note={lastVisit(plan, null).note} />
            <Fact label="Next visit" value={nextVisit(plan, null).value} kind="due" note={nextVisit(plan, null).note} />
          </dl>
        </section>
      ) : scheduling.length === 0 ? (
        <section className="pv-card">
          <div className="pv-card-head">
            <div>
              <h2>Visit schedule</h2>
              <p className="why">Nothing scheduled for this facility.</p>
            </div>
          </div>
          <TideState
            kind="empty"
            reason="none"
            message="This building has no visit plan yet. That usually means Scheduling has not put it on a rotation, rather than that a visit was missed."
          />
        </section>
      ) : (
        scheduling.map((row, i) => (
          <ScheduleCard
            // Scheduling rows carry no id of their own in this payload; the
            // index is the only stable handle, and the list is not reordered.
            key={`sched-${i}`}
            row={row}
            index={i}
            total={scheduling.length}
            plan={plan}
            link={link}
          />
        ))
      )}

      <section className="pv-card">
        <div className="pv-card-head">
          <div>
            <h2>Facility</h2>
            <p className="why">Where you are going and who to reach there.</p>
          </div>
          {facility.credentialing_status && (
            <FilledStatusChip word={facility.credentialing_status} column={SNF_CREDENTIALING} size="sm" />
          )}
        </div>
        {/*
          The address first, then the county — both equally important to the
          person driving there (April Torino, 2026-09-08). Both were already
          synced; they had never been projected.
        */}
        <div className="pv-address">
          <strong>{facility.address || 'Street address not recorded'}</strong>
          <span>
            {[[facility.city, facility.state].filter(Boolean).join(', '), facility.zip_code].filter(Boolean).join(' ') || 'City not recorded'}
            {facility.county ? ` · ${facility.county} County` : ' · County not recorded'}
          </span>
        </div>
        <dl className="pv-facts">
          <div className="pv-fact">
            <dt>Phone</dt>
            <dd className={facility.phone ? '' : 'none'}>{facility.phone || 'Not recorded'}</dd>
          </div>
          {/*
            Two bed fields on the board, two concepts, each under its own
            name. They are not merged here: the board's Licensed Beds is what
            audiology plans against; "# of Beds" is a different column. When
            Licensed Beds is blank and the other is filled, the other is shown
            and labelled as what it is, so nobody reads 104 as licensed when
            the board has not said so.
          */}
          <div className="pv-fact">
            <dt>Licensed beds</dt>
            <dd className={facility.licensed_beds ? 'num' : 'none'}>
              {facility.licensed_beds || 'Not recorded on the board'}
              {!facility.licensed_beds && facility.beds != null && (
                <span className="rel"># of Beds (board): {facility.beds}</span>
              )}
            </dd>
          </div>
          <div className="pv-fact">
            <dt>Enrolled residents</dt>
            <dd className={facility.enrolled_residents != null ? 'num' : 'none'}>
              {facility.enrolled_residents ?? '—'}
              <span className="rel">Residents here with an enrolled PA lead</span>
            </dd>
          </div>
          <div className="pv-fact">
            <dt>Check in with</dt>
            <dd className={sheet.contacts.ssd.length ? '' : 'none'}>
              {sheet.contacts.ssd.length
                ? sheet.contacts.ssd.map((c) => (
                  <span key={c.id} className="pv-contact">
                    <strong>{c.name || 'Name not recorded'}</strong> · SSD
                    {c.phone ? <span className="rel">{c.phone}</span> : null}
                  </span>
                ))
                : 'SSD contact not recorded'}
            </dd>
          </div>
        </dl>
      </section>

      <div className="pv-foot">
        <div className="pv-note">
          <span>
            Visits are planned by Scheduling in the eMerge Scheduling workspace; a date marked
            &ldquo;from the board&rdquo; is the last Monday record for a building with no
            eMerge plan yet. Nothing here is edited by a provider — ask Scheduling to move a visit.
          </span>
        </div>
      </div>
    </>
  );
}
